import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { ISmallCertificate } from '@sharedInterfaces/IWhoIAm';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import changeMapCertificate from '@src/APIs/graphQl/Certificate/changeMapCertificate';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import { useMapCertificateDialog } from '@src/App/Components/Dialogs/MapCertificateDialog/MapCertificateDialog';
import { setEmployeeCertificates } from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import { AlertTriangle, Award, Building, Calendar, Clock, Eye, FileText, Plus, Search, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// import { useCertificateOverlay } from '@src/App/Components/Overlays/CertificateOverlay/CertificateOverlay';

interface EditCertificateListProps {
    title: string;
    mode: 'edit' | 'add';
    certificates: { title: string; id?: number }[] | undefined;
    allCertificates: ISmallCertificate[];
    onChange?: (changed: true) => void;
}

function EditCertificateList(props: EditCertificateListProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const mapCertificateDialog = useMapCertificateDialog();
    const showErrorDialog = useErrorDialog();
    // const showCertificateOverlay = useCertificateOverlay();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).certificates;
    const offline = useSelector((state: AppState) => state.client.offline);
    const employee = useSelector((state: AppState) => state.employee);

    const [addedCerts, setAddedCerts] = useState<string[]>([]);
    const [deletingIds, setDeletingIds] = useState<number[]>([]);
    const [addingCerts, setAddingCerts] = useState<string[]>([]);
    const [certificates, setCertificates] = useState<{ title: string; id?: number }[]>([]);
    const [searchValue, setSearchValue] = useState<ISmallCertificate | string | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (props.certificates && props.mode === 'add') {
            let tempCerts = props.certificates.slice();
            tempCerts = tempCerts.filter((c) => !addedCerts.find((a) => c.title === a));
            tempCerts.sort((a, b) => a.title.localeCompare(b.title));
            setCertificates(tempCerts);
        } else {
            const tempCerts = employee ? [...employee.certificates] : [];
            tempCerts.sort((a, b) => a.title.localeCompare(b.title));
            setCertificates(tempCerts);
        }
    }, [addedCerts, employee, props.certificates, props.mode]);

    // Mockup-Daten für Zertifikatsstatus
    const getMockCertificateStatus = (certId: number) => {
        const mockStatuses = {
            1: { status: 'new', endDate: null },
            2: { status: 'expiring', endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) }, // 30 Tage
            3: { status: 'expired', endDate: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000) }, // 10 Tage her
            4: { status: 'valid', endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000) }, // 1 Jahr
            5: { status: 'expiring', endDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000) }, // 60 Tage
            6: { status: 'expired', endDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) }, // 30 Tage her
        };
        return mockStatuses[certId as keyof typeof mockStatuses] || { status: 'valid', endDate: null };
    };

    const isExpiringSoon = (endDate?: Date | null) => {
        if (!endDate) return false;
        const diffTime = endDate.getTime() - new Date().getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 90 && diffDays > 0;
    };

    const isExpired = (endDate?: Date | null) => {
        if (!endDate) return false;
        return endDate < new Date();
    };

    const availableCertificates = useMemo(() => {
        // Im Edit-Modus: Alle Zertifikate anzeigen (auch bereits vorhandene)
        // Im Add-Modus: Nur noch nicht hinzugefügte Zertifikate
        if (props.mode === 'edit') {
            return props.allCertificates;
        }
        return props.allCertificates.filter(
            (cert) => !certificates.find((existing) => existing.id === cert.id || existing.title === cert.title),
        );
    }, [props.allCertificates, certificates, props.mode]);

    const handleAdd = async (title: string, id?: number) => {
        if (!title.trim()) {
            setError(langStrings.pleaseEnterAName || 'Bitte geben Sie einen Namen ein');
            return;
        }

        setError(null);
        setInputValue('');

        if (props.mode === 'add') {
            setAddingCerts((prev) => [...prev, title]);
        }

        try {
            const result = await mapCertificateDialog(id, id ? undefined : title);
            if (result) {
                setAddedCerts((prev) => [...prev, title]);
                props.onChange?.(true);
            } else {
                setAddingCerts((prev) => prev.filter((t) => t !== title));
            }
        } catch (error) {
            setAddingCerts((prev) => prev.filter((t) => t !== title));
            showErrorDialog(error as Error, langStrings.errorAdd);
        }
    };

    const handleRemove = async (certificate: { title: string; id?: number }) => {
        if (!certificate.id) return;

        setDeletingIds((prev) => [...prev, certificate.id!]);

        try {
            await changeMapCertificate(certificate.id, false, undefined, undefined);
            setDeletingIds((prev) => prev.filter((d) => d !== certificate.id));
            dispatch(setEmployeeCertificates(employee.certificates.filter((c) => c.id !== certificate.id)));
            props.onChange?.(true);
        } catch (error) {
            setDeletingIds((prev) => prev.filter((d) => d !== certificate.id));
            showErrorDialog(error as Error, langStrings.errorRemove);
        }
    };

    if (!employee) return null;

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                overflow: 'hidden',
            }}
        >
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {props.title}
                </Typography>

                <Autocomplete
                    value={searchValue}
                    onChange={(_, newValue) => {
                        if (newValue) {
                            if (typeof newValue === 'string') {
                                handleAdd(newValue);
                            } else {
                                handleAdd(newValue.title, newValue.id);
                            }
                            setSearchValue(null);
                            setInputValue('');
                        }
                    }}
                    inputValue={inputValue}
                    onInputChange={(_, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    options={availableCertificates}
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
                    freeSolo
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={'Zertifikat suchen oder hinzufügen...'}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: <Search size={20} style={{ marginRight: 8, opacity: 0.6 }} />,
                                endAdornment: (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleAdd(inputValue)}
                                        disabled={!inputValue.trim() || offline}
                                        sx={{ mr: 1 }}
                                    >
                                        <Plus size={16} />
                                    </IconButton>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Award size={16} style={{ opacity: 0.6 }} />
                            <Typography>{typeof option === 'string' ? option : option.title}</Typography>
                        </Box>
                    )}
                    sx={{ mb: 2 }}
                />
            </Box>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2, mt: 0 }}>
                    {error}
                </Alert>
            )}

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>{langStrings.certificate || 'Zertifikat'}</TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 120 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Building size={14} />
                                    Hersteller
                                </Box>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 100 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Clock size={14} />
                                    Laufzeit
                                </Box>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, width: 120 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Calendar size={14} />
                                    Ablaufdatum
                                </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, width: 120 }}>
                                {'Aktionen'}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {certificates.map((certificate) => {
                            // Find full certificate data for additional info
                            const fullCert = props.allCertificates.find((c) => c.id === certificate.id);
                            const mockStatus = getMockCertificateStatus(certificate.id as number);
                            const expired = isExpired(mockStatus.endDate);
                            const expiringSoon = isExpiringSoon(mockStatus.endDate);

                            return (
                                <TableRow
                                    key={certificate.id || certificate.title}
                                    sx={{
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                                        },
                                        ...(expired && {
                                            bgcolor: alpha(theme.palette.error.main, 0.05),
                                        }),
                                    }}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Award size={16} style={{ opacity: 0.6 }} />
                                            <Box>
                                                <Typography>{certificate.title}</Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                                    {mockStatus.status === 'new' && (
                                                        <Chip
                                                            label="Neu"
                                                            size="small"
                                                            color="primary"
                                                            sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                    )}
                                                    {expired && (
                                                        <Chip
                                                            label="Abgelaufen"
                                                            size="small"
                                                            color="error"
                                                            icon={<AlertTriangle size={12} />}
                                                            sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                    )}
                                                    {expiringSoon && !expired && (
                                                        <Chip
                                                            label="Läuft ab"
                                                            size="small"
                                                            color="warning"
                                                            icon={<AlertTriangle size={12} />}
                                                            sx={{ height: 18, fontSize: '0.65rem' }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            {certificate.id && certificate.id <= 6
                                                ? ['Microsoft', 'AWS', 'Google', 'Oracle', 'Cisco', 'Adobe'][
                                                      certificate.id - 1
                                                  ]
                                                : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            {fullCert?.durationInDays
                                                ? `${Math.round(fullCert.durationInDays / 365)} Jahre`
                                                : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                opacity: 0.8,
                                                color: expired
                                                    ? theme.palette.error.main
                                                    : expiringSoon
                                                      ? theme.palette.warning.main
                                                      : 'inherit',
                                            }}
                                        >
                                            {mockStatus.endDate ? mockStatus.endDate.toLocaleDateString('de-DE') : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                            {certificate.id && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/certificates/${certificate.id}`)}
                                                    sx={{
                                                        color: theme.palette.info.main,
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.info.main, 0.1),
                                                        },
                                                    }}
                                                >
                                                    <Eye size={16} />
                                                </IconButton>
                                            )}

                                            {props.mode === 'edit' && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemove(certificate)}
                                                    disabled={deletingIds.includes(certificate.id as number) || offline}
                                                    sx={{
                                                        color: theme.palette.error.main,
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.error.main, 0.1),
                                                        },
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            )}
                                            {props.mode === 'add' && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleAdd(certificate.title, certificate.id)}
                                                    disabled={addingCerts.includes(certificate.title) || offline}
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                        },
                                                    }}
                                                >
                                                    <Plus size={16} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {certificates.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 2,
                                            opacity: 0.7,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1,
                                            }}
                                        >
                                            <FileText size={32} style={{ opacity: 0.6 }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                                            Noch keine Zertifikate
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ opacity: 0.7, mb: 2, textAlign: 'center', maxWidth: 300 }}
                                        >
                                            Fügen Sie Ihr erstes Zertifikat hinzu, um Ihre Qualifikationen zu verwalten
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<Plus size={16} />}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                px: 3,
                                                py: 1.5,
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            Zertifikat hinzufügen
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export interface INewCertificate {
    id?: number;
    title: string;
}

export default EditCertificateList;
